import { motion } from "framer-motion";
import { useState } from "react";
import { BookOpen, Quote, Share2, Loader2, ImageDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";
import ListenButton from "@/components/ListenButton";
import FavoriteButton from "@/components/FavoriteButton";
import CardDialog from "@/components/CardDialog";
import SmartImage from "@/components/SmartImage";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export default function AnswerView({ answer, streaming = false, imageLoading = false }) {
  const [cardOpen, setCardOpen] = useState(false);
  const paragraphs = (answer.story || "").split(/\n\n+/).filter(Boolean);
  const imgSrc = answer.image_url ? `${BACKEND_URL}${answer.image_url}` : null;
  const showControls = !streaming && answer.id;

  const share = async () => {
    const url = `${API}/share/${answer.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share this answer with anyone.");
    } catch {
      toast.message(url);
    }
  };

  return (
    <article data-testid="answer-view" className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-6 py-14 lg:grid-cols-12 lg:py-20">
      {/* Illustration */}
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          {imgSrc ? (
            <SmartImage
              testId="ai-illustration"
              src={imgSrc}
              alt={answer.title}
              className="aspect-[4/5] w-full rounded-md shadow-[0_20px_60px_rgba(140,106,71,0.18)] ring-1 ring-border"
              imgClassName="object-cover"
            />
          ) : (
            <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-3 rounded-md bg-muted ring-1 ring-border">
              {imageLoading ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground">Painting the scene...</span>
                </>
              ) : (
                <BookOpen className="h-10 w-10 text-muted-foreground" />
              )}
            </div>
          )}
          <p className="mt-4 text-center font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground">
            An illustration of the scene
          </p>
          {showControls && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <ListenButton qaId={answer.id} />
              <FavoriteButton answer={answer} />
              <Button data-testid="share-button" onClick={share} variant="outline" className="rounded-full font-sans">
                <Share2 className="mr-2 h-4 w-4" /> Share
              </Button>
              <Button data-testid="card-button" onClick={() => setCardOpen(true)} variant="outline" className="rounded-full font-sans">
                <ImageDown className="mr-2 h-4 w-4" />
                Save Card
              </Button>
            </div>
          )}
          {showControls && (
            <CardDialog
              open={cardOpen}
              onOpenChange={setCardOpen}
              endpoint={`${API}/answer/${answer.id}/card.png`}
              filenamePrefix="askyeshua"
              shareText={`"${answer.verses?.[0]?.text || answer.title}" — ${answer.verses?.[0]?.reference || ""} · AskYeshua`}
              shareUrl={`${API}/share/${answer.id}`}
            />
          )}
        </div>
      </div>

      {/* Text */}
      <motion.div variants={container} initial="hidden" animate="show" className="lg:col-span-6 lg:col-start-7">
        <motion.div variants={item}>
          <span className="font-sans text-xs uppercase tracking-[0.25em] text-primary">In answer to your question</span>
          <h1 data-testid="answer-title" className="mt-3 min-h-[1.2em] font-serif text-4xl font-600 leading-tight tracking-tight text-foreground sm:text-5xl">
            {answer.title}
            {streaming && !answer.title && <span className="inline-block h-8 w-2 animate-pulse bg-primary/60 align-middle" />}
          </h1>
          {answer.question && <p className="mt-3 font-sans text-sm italic text-muted-foreground">"{answer.question}"</p>}
        </motion.div>

        {answer.intro && (
          <motion.p variants={item} className="mt-8 font-body text-lg leading-relaxed text-foreground/90">
            {answer.intro}
          </motion.p>
        )}

        <div data-testid="story-container" className="mt-6 space-y-6">
          {paragraphs.map((p, i) => (
            <motion.p
              variants={item}
              key={i}
              className={`font-body text-[1.05rem] leading-[1.9] text-foreground/85 ${i === 0 ? "drop-cap" : ""}`}
            >
              {p}
              {streaming && i === paragraphs.length - 1 && (
                <span className="ml-0.5 inline-block h-4 w-[3px] animate-pulse bg-primary/60 align-middle" />
              )}
            </motion.p>
          ))}
        </div>

        {answer.verses?.length > 0 && (
          <motion.div variants={item} className="mt-10 space-y-5">
            {answer.verses.map((v, i) => (
              <blockquote
                data-testid="kjv-verse"
                key={i}
                className="relative rounded-r-md border-l-4 border-primary bg-muted/50 p-6"
              >
                <Quote className="absolute right-4 top-4 h-5 w-5 text-accent" />
                <p className="font-serif text-xl italic leading-relaxed text-foreground/90">"{v.text}"</p>
                <cite className="mt-3 block font-sans text-xs uppercase not-italic tracking-[0.18em] text-primary">
                  {v.reference}
                </cite>
              </blockquote>
            ))}
          </motion.div>
        )}

        {answer.reflection && (
          <motion.div variants={item} className="mt-10 border-t border-border pt-8">
            <span className="font-sans text-xs uppercase tracking-[0.25em] text-primary">Reflection</span>
            <p className="mt-3 font-body text-lg italic leading-relaxed text-foreground/85">{answer.reflection}</p>
          </motion.div>
        )}
      </motion.div>
    </article>
  );
}
